import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMeetingStore } from '@stores/meeting'
import { APP_FRONTEND_ROUTES, ROUTES } from '@constants/routes'
import { useUpdateChimeRatingMutation } from '@/models'

type MeetingMode = 'phone' | 'video'

type UseMeetingLeftLogicReturn = {
  appointmentId: string | null
  rating: number | null
  isSubmitting: boolean
  shouldRedirect: boolean
  handleRatingChange: (newRating: number) => Promise<void>
  handleReturnToWaitingRoom: () => void
  handleEvaluateAppointment: () => void
}

export default function useMeetingLeftLogic(mode: MeetingMode): UseMeetingLeftLogicReturn {
  const navigate = useNavigate()
  const appointmentId = useMeetingStore((state) => state.appointmentId)
  const [rating, setRating] = useState<number | null>(null)
  const [updateChimeRating, { loading: isSubmitting }] = useUpdateChimeRatingMutation()

  const shouldRedirect = !appointmentId

  useEffect(() => {
    if (shouldRedirect) {
      navigate(ROUTES.home, { replace: true })
    }
  }, [shouldRedirect, navigate])

  const handleReturnToWaitingRoom = () => {
    if (!appointmentId) return
    const route = mode === 'phone' ? ROUTES.phoneWaitingRoom(appointmentId) : ROUTES.videoWaitingRoom(appointmentId)
    navigate(route)
  }

  const handleEvaluateAppointment = () => {
    if (!appointmentId) return
    window.location.href = APP_FRONTEND_ROUTES.review(appointmentId)
  }

  const handleRatingChange = async (newRating: number) => {
    if (!appointmentId || isSubmitting) return

    const previousRating = rating
    setRating(newRating)

    try {
      await updateChimeRating({
        variables: {
          appointmentId,
          rating: newRating
        }
      })
    } catch (error) {
      console.error('Error updating chime rating:', error)
      setRating(previousRating)
    }
  }

  return {
    appointmentId,
    rating,
    isSubmitting,
    shouldRedirect,
    handleRatingChange,
    handleReturnToWaitingRoom,
    handleEvaluateAppointment
  }
}
